import classNames from "classnames";
import { DUCK_CHAT_MAX_WIDTH } from "duck/ui/constants";
import { useTranslation } from "react-i18next";
import SouthIcon from "@mui/icons-material/South";
import Box from "@mui/material/Box";

import { useViewport } from "shared/contexts/ViewportContext";

import IconButton from "features/components/IconButton";
import Typography from "features/components/Typography";
import QuackStarIcon from "features/ui/Icons/QuackStarIcon";

import LoadingSpinner from "./components/LoadingSpinner";
import Messages from "./components/Messages";
import { useChatContext } from "./contexts/useChatContext";
import { useChatMessagesScrolling } from "./hooks";
import { scrollToBottom } from "./utils";

const ChatMessages = () => {
  const { t } = useTranslation("duck");
  const {
    messages,
    isStreaming,
    currentThreadId,
    followupSuggestions,
    isLoadingCurrentThread,
  } = useChatContext();
  const { isMobile } = useViewport();

  const {
    containerRef,
    mostRecentUserMessageRef,
    spacerRef,
    spacerHeight,
    lastUserMessageIndex,
    showScrollToBottom,
  } = useChatMessagesScrolling({
    messages,
    isStreaming,
    currentThreadId,
    followupSuggestions,
  });

  return (
    <div
      className={classNames("relative flex flex-col min-h-0 flex-1", {
        "justify-end": messages.length === 0 && !isMobile,
      })}
    >
      <Box
        ref={containerRef}
        className={classNames(
          "overflow-y-auto p-4 flex flex-col w-full justify-center items-center",
          {
            "h-fit": messages.length == 0,
            "flex-1 h-full": messages.length > 0,
          }
        )}
        data-testid="duck-chat-messages"
      >
        <div
          className="gap-2 flex flex-col w-full min-h-full"
          style={{ maxWidth: DUCK_CHAT_MAX_WIDTH }}
        >
          {/* Always show initial greeting at the top */}
          <div className="flex flex-col justify-center items-center mt-20 mb-20">
            <div className={isLoadingCurrentThread ? "grayscale" : ""}>
              <QuackStarIcon />
            </div>
            <div className="mt-2 flex items-center gap-3">
              {isLoadingCurrentThread ? (
                <LoadingSpinner />
              ) : (
                <Typography variant="h4">{t("initial_prompt")}</Typography>
              )}
            </div>
          </div>

          <Messages
            messages={messages}
            isStreaming={isStreaming}
            lastUserMessageIndex={lastUserMessageIndex}
            mostRecentUserMessageRef={mostRecentUserMessageRef}
            threadId={currentThreadId ?? undefined}
          />

          {/* Spacer to push messages up so user message appears at top */}
          <div
            ref={spacerRef}
            className="shrink-0 w-full pointer-events-none"
            style={{ height: `${spacerHeight}px` }}
          />
        </div>
      </Box>
      {showScrollToBottom && (
        <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 z-10">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              scrollToBottom(containerRef, spacerHeight);
            }}
            data-testid="button-download-action"
            size="small"
            className="!rounded-md !border !border-gray-200 !shadow-sm !bg-white"
          >
            <SouthIcon fontSize="small" className="!text-gray-700" />
          </IconButton>
        </div>
      )}
    </div>
  );
};

export default ChatMessages;
